import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import { SelectionGroup, CheckboxList } from '@grandvisionhq/common';
import { Heading } from '@grandvisionhq/elements';
import { useIntl } from '@grandvisionhq/state';
import LensPacks from './components/LensPacks/LensPacks.js';
import { PriceAside } from './components/PriceAside/PriceAside.js';
import { getInitialLensPack, findAndSortBenefits } from './utils/lens-pack.js';
import { monoSorting, multiSorting } from './constants/sorting.js';
import { calcGlassesPrice, calcGlassesPriceDifference, calcLensPackPrice, calcSelectedExtrasPrice } from './utils/calculation.js';
import { isGlassesType, isExtraType } from './types/typeguards.js';

const SSP = ({ className , frameData , lensPackData , monofocalExtras , multifocalExtras , lensExtrasResolver , useIncludedLensPack , initialGlasses ='frameAndMonofocal' , initialLensPack , initialExtras =[] , useIncludedFrame , onChange ,  })=>{
    var ref;
    const { currency , getLabel  } = useIntl();
    const [selectedGlasses1, setSelectedGlasses] = React.useState(initialGlasses);
    const [selectedExtras1, setSelectedExtras] = React.useState(initialExtras);
    const initialLensPackType = initialLensPack || getInitialLensPack(lensPackData.monofocalLensPacks).pack.key;
    const [selectedLensPack1, setSelectedLensPack] = React.useState(initialLensPackType);
    const getExtrasData = (selectedGlasses, selectedLensPack, selectedExtras)=>{
        const allowedExtras = lensExtrasResolver(selectedGlasses, selectedLensPack, selectedExtras, frameData);
        let extrasData = selectedGlasses === 'frameAndMonofocal' ? monofocalExtras : multifocalExtras;
        extrasData = extrasData.map((extra)=>{
            const enabled = allowedExtras && allowedExtras.indexOf(extra.value) > -1;
            const checked = enabled && selectedExtras && selectedExtras.indexOf(extra.value) > -1;
            return swcHelpers.objectSpread({}, extra, {
                isChecked: checked,
                disabled: !enabled
            });
        });
        return extrasData;
    };
    const includedLensPackPrice = ((ref = lensPackData.includedLensPack) === null || ref === void 0 ? void 0 : ref.priceInCents) || 0;
    const initialPrice = useIncludedLensPack ? includedLensPackPrice + frameData.framePrice : frameData.framePrice;
    const frameAndMonofocalPrice = initialPrice;
    const initLensPackMultifocal = getInitialLensPack(lensPackData.multifocalLensPacks);
    const initialLensPackMultifocalPrice = useIncludedLensPack && initLensPackMultifocal ? initLensPackMultifocal.priceInCents || 0 : 0;
    const frameAndMultifocalPrice = !frameData.isKidsGlasses ? initialLensPackMultifocalPrice + initialPrice : 0;
    const prices = {
        frameAndMonofocalPrice,
        frameAndMultifocalPrice,
        framePrice: frameData.framePrice
    };
    const createGlassesItems = ()=>{
        const glassesTypes = [
            'frameOnly',
            'frameAndMonofocal',
            'frameAndMultifocal'
        ];
        const glassesItems = glassesTypes.filter((glassesType)=>{
            // Only NON-kids frames can have multifocal.
            return !(glassesType === 'frameAndMultifocal' && frameData.isKidsGlasses);
        }).map((glassesType)=>{
            let price = calcGlassesPrice(glassesType, prices);
            let prefix = '';
            if (useIncludedFrame) {
                price = calcGlassesPriceDifference(glassesType, initialGlasses, prices);
                prefix = price < 0 ? '-' : price > 0 ? '+' : '';
            }
            return {
                title: getLabel(`products.ssp.glasses.${glassesType}.title`),
                text: getLabel(`products.ssp.glasses.${glassesType}.text`),
                value: glassesType,
                scope: 'glasses',
                aside: useIncludedFrame && price === 0 ? /*#__PURE__*/ React.createElement("div", null, getLabel('products.included')) : /*#__PURE__*/ React.createElement(PriceAside, {
                    prefix: prefix,
                    price: Math.abs(price),
                    currency: currency
                }),
                disabled: false
            };
        });
        return glassesItems;
    };
    const filterByAllowed = (glasses, lensPack, extras)=>{
        // Get allowed extras based on the value and assign to newExtras
        let allowedExtras = lensExtrasResolver(glasses, lensPack, extras, frameData);
        let newExtras = extras.filter((item)=>allowedExtras.indexOf(item) > -1
        );
        // With the newExtras, check allowedExtras again and filter it with the newExtras itself
        allowedExtras = lensExtrasResolver(glasses, lensPack, newExtras, frameData);
        newExtras = extras.filter((item)=>allowedExtras.indexOf(item) > -1
        );
        return newExtras;
    };
    const handleGlassesTypeChange = (type)=>{
        if (!isGlassesType(type)) {
            console.error(`Invalid glasses type: "${type}"`);
            return;
        }
        const extrasData = getExtrasData(type, selectedLensPack1, selectedExtras1);
        let newExtras = [];
        if (type === 'frameOnly') {
            newExtras = [];
        } else {
            newExtras = filterByAllowed(type, selectedLensPack1, selectedExtras1);
        }
        const newGlassesPrice = calcGlassesPrice(type, prices);
        const newLensPackPrice = calcLensPackPrice(frameData, lensPackData, type, selectedLensPack1, useIncludedLensPack);
        const newExtrasPrice = calcSelectedExtrasPrice(extrasData, newExtras);
        const totalPrice = newGlassesPrice + newLensPackPrice + newExtrasPrice;
        setSelectedGlasses(type);
        onChange && onChange(totalPrice);
    };
    const handleLensPackChange = (pack)=>{
        const extrasData = getExtrasData(selectedGlasses1, pack, selectedExtras1);
        const newExtras = filterByAllowed(selectedGlasses1, pack, selectedExtras1);
        const newGlassesPrice = calcGlassesPrice(selectedGlasses1, prices);
        const newLensPackPrice = calcLensPackPrice(frameData, lensPackData, selectedGlasses1, pack, useIncludedLensPack);
        const newExtrasPrice = calcSelectedExtrasPrice(extrasData, newExtras);
        const totalPrice = newGlassesPrice + newLensPackPrice + newExtrasPrice;
        setSelectedLensPack(pack);
        onChange && onChange(totalPrice);
    };
    const handleExtrasChange = (items)=>{
        const extras = items.filter((item)=>isExtraType(item.value)
        );
        const values = extras.map((i)=>i.value
        );
        const newExtras = filterByAllowed(selectedGlasses1, selectedLensPack1, values);
        const extrasData = getExtrasData(selectedGlasses1, selectedLensPack1, newExtras);
        const newGlassesPrice = calcGlassesPrice(selectedGlasses1, prices);
        const newLensPackPrice = calcLensPackPrice(frameData, lensPackData, selectedGlasses1, selectedLensPack1, useIncludedLensPack);
        const newExtrasPrice = calcSelectedExtrasPrice(extrasData, newExtras);
        const totalPrice = newGlassesPrice + newLensPackPrice + newExtrasPrice;
        setSelectedExtras(newExtras);
        onChange && onChange(totalPrice);
    };
    const { monofocalLensPacks , multifocalLensPacks  } = lensPackData;
    const activePacks = selectedGlasses1 === 'frameAndMonofocal' ? monofocalLensPacks : multifocalLensPacks;
    const extrasData1 = getExtrasData(selectedGlasses1, selectedLensPack1, selectedExtras1);
    const glassesItems1 = createGlassesItems();
    const sorting = selectedGlasses1 === 'frameAndMonofocal' ? monoSorting : multiSorting;
    const packsWithBenefits = findAndSortBenefits(activePacks, sorting);
    const withVisualField = packsWithBenefits.some((pack)=>pack.visualField
    );
    const withVoucherValues = packsWithBenefits.some((pack)=>pack.voucherValue
    );
    const SSPclasses = classnames({
        ssp: true,
        'ssp--with-voucher': withVoucherValues,
        'ssp--with-visual-field': withVisualField,
        className
    });
    const selectedItems = selectedExtras1.reduce((acc, name)=>{
        const extra = extrasData1.find((e)=>e.value === name
        );
        return extra ? [
            ...acc,
            extra
        ] : acc;
    }, []);
    return /*#__PURE__*/ React.createElement("div", {
        className: SSPclasses
    }, /*#__PURE__*/ React.createElement("div", {
        className: "site-container ssp__container"
    }, /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Heading, {
        variant: "small",
        className: "ssp__section-title"
    }, getLabel('products.ssp.glasses.title')), /*#__PURE__*/ React.createElement(SelectionGroup, {
        items: glassesItems1,
        selectedItem: selectedGlasses1,
        onChange: handleGlassesTypeChange
    }), selectedGlasses1 !== 'frameOnly' && /*#__PURE__*/ React.createElement(React.Fragment, null, getLabel('products.ssp.lenses.title') && /*#__PURE__*/ React.createElement(Heading, {
        variant: "small",
        className: "ssp__section-title"
    }, getLabel('products.ssp.lenses.title')), /*#__PURE__*/ React.createElement(LensPacks, {
        packs: packsWithBenefits,
        selectedPack: selectedLensPack1 || 'bronze',
        selectedGlasses: selectedGlasses1,
        onChange: handleLensPackChange,
        useIncludedLensPack: useIncludedLensPack
    }), extrasData1.length > 0 && /*#__PURE__*/ React.createElement(React.Fragment, null, getLabel('products.ssp.extras.title') && /*#__PURE__*/ React.createElement(Heading, {
        variant: "small",
        className: "ssp__section-title"
    }, getLabel('products.ssp.extras.title')), /*#__PURE__*/ React.createElement(CheckboxList, {
        checkboxAlignment: "middle",
        className: "ssp__lens-extras",
        itemClassName: "ssp__lens-extras-item",
        idPrefix: "ssp-lens-extra__",
        items: extrasData1,
        selectedItems: selectedItems,
        getItemIdentifier: (item)=>item.value
        ,
        isItemDisabled: (item)=>item.disabled
        ,
        renderItem: (item)=>/*#__PURE__*/ React.createElement("div", {
                className: classnames('ssp-lens-extra__content', {
                    'ssp-lens-extra__content--disabled': item.disabled
                })
            }, /*#__PURE__*/ React.createElement("div", {
                className: "ssp-lens-extra__main"
            }, /*#__PURE__*/ React.createElement("span", {
                className: "ssp-lens-extra__title"
            }, item.title), /*#__PURE__*/ React.createElement("span", {
                className: "ssp-lens-extra__text"
            }, item.text)), /*#__PURE__*/ React.createElement(PriceAside, {
                prefix: "+",
                price: item.price,
                currency: currency,
                className: "ssp-lens-extra__price"
            }))
        ,
        onChange: handleExtrasChange
    }))))));
};
var SSP$1 = SSP;

export { SSP, SSP$1 as default };
//# sourceMappingURL=SSP.js.map
